/**
 * Klaviyo Back-In-Stock Quickbuy Handler
 * 
 * This script ensures Klaviyo's BIS feature works correctly in quickbuy modals,
 * especially when opened from collection pages where URLs contain collection paths.
 * 
 * Features:
 * - Forces clean product URLs for Klaviyo
 * - Handles variant changes in quickbuy modals
 * - Ensures Klaviyo's native BIS button appears correctly
 * - Works with dynamically loaded content
 */

(function() {
  'use strict';

  // Force Klaviyo to use clean product URL (without collection paths)
  function forceCleanKlaviyoURL(productHandle) {
    if (!window._klOnsite || !productHandle) return;

    // Clean URL in Shopify format
    const cleanUrl = `/products/${productHandle}`;

    // Patch Klaviyo's page data (in memory only - doesn't affect browser URL)
    if (window._klOnsite.page) {
      window._klOnsite.page.url = cleanUrl;
      window._klOnsite.page.pathname = cleanUrl;
    }

    // BIS specifically checks this
    if (window._klOnsite.product) {
      window._klOnsite.product.url = cleanUrl;
    }
  }

  // Force Klaviyo to re-initialize BIS detection in a container
  function triggerKlaviyoBISDetection(container, productHandle) {
    if (!container || !productHandle) return;
    
    // Ensure clean URL first
    forceCleanKlaviyoURL(productHandle);
    
    // Note: We don't call initBIS as it's not a valid Klaviyo method
    // Instead, we rely on Klaviyo's automatic detection of the button
    
    // Find all klaviyo-bis-trigger elements
    const triggers = container.querySelectorAll('.klaviyo-bis-trigger');
    
    triggers.forEach(trigger => {
      // Skip if disabled
      if (trigger.hasAttribute('data-klaviyo-disabled')) return;
      
      // Ensure trigger has identifier
      const form = container.querySelector('form[id*="product-form"]');
      if (form && !trigger.hasAttribute('data-a11y-identifier')) {
        const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
        trigger.setAttribute('data-a11y-identifier', `bis-trigger-${variantId}`);
      }
      
      // Temporarily remove and re-add to trigger Klaviyo's MutationObserver
      const parent = trigger.parentNode;
      const nextSibling = trigger.nextSibling;
      
      if (parent) {
        // Remove
        parent.removeChild(trigger);
        
        // Re-add after a brief moment
        requestAnimationFrame(() => {
          if (nextSibling) {
            parent.insertBefore(trigger, nextSibling);
          } else {
            parent.appendChild(trigger);
          }
          
          // Force clean URL again after re-adding
          forceCleanKlaviyoURL(productHandle);
          
          // Also ensure button inside has identifier
          const button = trigger.querySelector('button');
          if (button && !button.hasAttribute('data-a11y-identifier')) {
            const form = container.querySelector('form[id*="product-form"]');
            if (form) {
              const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
              button.setAttribute('data-a11y-identifier', `bis-button-${variantId}`);
            }
          }
        });
      }
    });
  }

  // Initialize Klaviyo BIS for quickbuy modal
  function initKlaviyoBISQuickbuy(modal) {
    if (!modal) return;

    const productHandle = modal.getAttribute('handle');
    if (!productHandle) return;

    // Extract handle if it's a full URL
    let cleanHandle = productHandle;
    if (productHandle.includes('/products/')) {
      const match = productHandle.match(/\/products\/([^\/\?]+)/);
      cleanHandle = match ? match[1] : productHandle;
    }

    // Force clean URL immediately when modal opens
    forceCleanKlaviyoURL(cleanHandle);
    
    // Force Klaviyo to re-scan for BIS buttons in the modal
    // This ensures Klaviyo's native BIS script detects the trigger element
    setTimeout(() => {
      // Trigger Klaviyo's BIS detection
      triggerKlaviyoBISDetection(modal, cleanHandle);
      
      // Also update Klaviyo's page data to trigger BIS scanner
      if (window._klOnsite && window._klOnsite.page) {
        window._klOnsite.page.url = `/products/${cleanHandle}`;
        window._klOnsite.page.pathname = `/products/${cleanHandle}`;
        
        // Trigger events that Klaviyo might listen to
        const pageUpdateEvent = new CustomEvent('klaviyo:page:update', { bubbles: true });
        document.dispatchEvent(pageUpdateEvent);
        
        const bisInitEvent = new CustomEvent('klaviyo:bis:init', { bubbles: true });
        modal.dispatchEvent(bisInitEvent);
      }
    }, 150);

    // Find all klaviyo-bis-trigger elements in the modal
    const klaviyoTriggers = modal.querySelectorAll('.klaviyo-bis-trigger');
    
    klaviyoTriggers.forEach(trigger => {
      // Skip if Klaviyo is disabled for this product
      if (trigger.hasAttribute('data-klaviyo-disabled')) {
        return;
      }

      const formId = trigger.getAttribute('data-klaviyo-form-id');
      if (!formId) return;

      const button = trigger.querySelector('button.buy-buttons__add-to-cart');
      if (!button) return;

      const form = button.closest('form');
      if (!form) return;
      
      // Ensure trigger element has identifier for Klaviyo compatibility
      const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
      const triggerIdentifier = `bis-trigger-${variantId}`;
      if (!trigger.hasAttribute('data-a11y-identifier')) {
        trigger.setAttribute('data-a11y-identifier', triggerIdentifier);
      }

      // Function to handle button click and trigger Klaviyo form
      const handleKlaviyoClick = function(e) {
        e.preventDefault();
        e.stopPropagation();

        // Force clean URL before triggering
        forceCleanKlaviyoURL(cleanHandle);

        // Trigger Klaviyo form
        if (window._klOnsite && typeof window._klOnsite.openForm === 'function') {
          window._klOnsite.openForm([formId]);
        } else if (window._klOnsite && typeof window._klOnsite.push === 'function') {
          window._klOnsite.push(['openForm', formId]);
        }
      };

      // Function to update button behavior based on variant availability
      const updateButtonBehavior = () => {
        const currentButton = trigger.querySelector('button.buy-buttons__add-to-cart');
        if (!currentButton) return;

        // Check if Klaviyo is still enabled
        if (trigger.hasAttribute('data-klaviyo-disabled')) {
          if (currentButton.hasAttribute('data-variant-unavailable') || currentButton.disabled) {
            currentButton.type = 'submit';
            currentButton.disabled = true;
          }
          return;
        }

        const isUnavailable = currentButton.hasAttribute('data-variant-unavailable') || currentButton.disabled;

        if (isUnavailable) {
          // Change button to trigger Klaviyo form
          currentButton.type = 'button';
          currentButton.removeAttribute('disabled');

          // Ensure button has data-a11y-identifier for Klaviyo compatibility
          if (!currentButton.hasAttribute('data-a11y-identifier')) {
            const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
            const identifier = `bis-button-${variantId}`;
            currentButton.setAttribute('data-a11y-identifier', identifier);
          }

          // Remove old click handler by cloning
          const newButton = currentButton.cloneNode(true);
          currentButton.parentNode.replaceChild(newButton, currentButton);

          // Ensure cloned button also has the identifier
          if (!newButton.hasAttribute('data-a11y-identifier')) {
            const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
            const identifier = `bis-button-${variantId}`;
            newButton.setAttribute('data-a11y-identifier', identifier);
          }

          // Add Klaviyo form trigger
          newButton.addEventListener('click', handleKlaviyoClick);

          // Also try to trigger Klaviyo's native BIS button if it exists
          // Klaviyo might inject its own button, so we check for it
          // Check multiple times as Klaviyo might inject it asynchronously
          let checkCount = 0;
          const maxChecks = 10;
          const checkInterval = setInterval(() => {
            checkCount++;
            const klaviyoNativeButton = modal.querySelector('[data-a11y-identifier*="bis-button"], #klaviyo-bis-button-container');
            if (klaviyoNativeButton) {
              // Klaviyo's native button exists - ensure clean URL is set
              if (cleanHandle) {
                forceCleanKlaviyoURL(cleanHandle);
              }
              clearInterval(checkInterval);
            } else if (checkCount >= maxChecks) {
              clearInterval(checkInterval);
            }
          }, 200);
        } else {
          // Restore normal button behavior
          currentButton.type = 'submit';
        }
      };

      // Initial check
      updateButtonBehavior();
      
      // Also ensure Klaviyo's BIS script sees this trigger element
      // By temporarily removing and re-adding the klaviyo-bis-trigger class
      setTimeout(() => {
        const originalClass = trigger.className;
        trigger.className = trigger.className.replace('klaviyo-bis-trigger', 'klaviyo-bis-trigger-temp');
        requestAnimationFrame(() => {
          trigger.className = originalClass;
          // Force clean URL again after re-adding
          if (cleanHandle) {
            forceCleanKlaviyoURL(cleanHandle);
          }
        });
      }, 150);

      // Listen for variant changes
      form.addEventListener('variant:change', () => {
        setTimeout(() => {
          // Update identifier with new variant ID
          const newVariantId = form.querySelector('input[name="id"]')?.value || Date.now();
          const newIdentifier = `bis-button-${newVariantId}`;
          const currentButton = trigger.querySelector('button.buy-buttons__add-to-cart');
          if (currentButton) {
            currentButton.setAttribute('data-a11y-identifier', newIdentifier);
          }
          trigger.setAttribute('data-a11y-identifier', `bis-trigger-${newVariantId}`);
          
          updateButtonBehavior();
          // Trigger Klaviyo BIS detection after variant change
          triggerKlaviyoBISDetection(modal, cleanHandle);
        }, 100);
      });

      form.addEventListener('product:rerender', () => {
        setTimeout(() => {
          updateButtonBehavior();
          // Re-initialize after rerender and trigger BIS detection
          triggerKlaviyoBISDetection(modal, cleanHandle);
          initKlaviyoBISQuickbuy(modal);
        }, 150);
      });
    });

    // Also watch for Klaviyo's native BIS button injection and ensure it has proper identifier
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if Klaviyo's native button container was injected
            const klaviyoContainer = node.matches && node.matches('#klaviyo-bis-button-container') 
              ? node 
              : (node.querySelector && node.querySelector('#klaviyo-bis-button-container'));
            
            // Also check for button with bis-button identifier
            const klaviyoNativeButton = node.matches && node.matches('[data-a11y-identifier*="bis-button"]') 
              ? node 
              : (node.querySelector && node.querySelector('[data-a11y-identifier*="bis-button"]'));
            
            if (klaviyoContainer || klaviyoNativeButton) {
              const targetElement = klaviyoContainer || klaviyoNativeButton;
              
              // Ensure clean URL is set for Klaviyo's native button
              if (cleanHandle) {
                forceCleanKlaviyoURL(cleanHandle);
              }
              
              // Find the actual button element
              const nativeButton = targetElement.querySelector('button[data-a11y-identifier*="bis-button"]') || targetElement.querySelector('button');
              
              if (nativeButton && nativeButton.tagName === 'BUTTON') {
                // Ensure the button has the data-a11y-identifier attribute
                if (!nativeButton.hasAttribute('data-a11y-identifier')) {
                  // Generate identifier similar to Klaviyo's format
                  const variantId = form.querySelector('input[name="id"]')?.value || 'default';
                  const identifier = `bis-button-${variantId}`;
                  nativeButton.setAttribute('data-a11y-identifier', identifier);
                  
                  // Also ensure parent container has it if it's the container
                  if (klaviyoContainer && !klaviyoContainer.hasAttribute('data-a11y-identifier')) {
                    klaviyoContainer.setAttribute('data-a11y-identifier', identifier);
                  }
                }
                
                // Add click handler to ensure clean URL
                nativeButton.addEventListener('click', function() {
                  if (cleanHandle) {
                    forceCleanKlaviyoURL(cleanHandle);
                  }
                }, { once: false });
              }
              
              // When Klaviyo's native button exists, ensure clean URL is set
              // We don't hide our custom button - let Klaviyo handle visibility
            }
            
            // Also check for any button that might need the identifier
            const allButtons = node.querySelectorAll ? node.querySelectorAll('button') : [];
            allButtons.forEach(btn => {
              // Check if this looks like a Klaviyo BIS button but is missing identifier
              const parent = btn.closest('#klaviyo-bis-button-container, [class*="klaviyo"], [class*="bis"]');
              const isBISButton = parent && btn.textContent && (
                btn.textContent.toLowerCase().includes('besked') || 
                btn.textContent.toLowerCase().includes('notify') ||
                btn.textContent.toLowerCase().includes('udsolgt')
              );
              
              if (isBISButton && !btn.hasAttribute('data-a11y-identifier')) {
                // Get variant ID from form
                const variantInput = form.querySelector('input[name="id"]');
                const variantId = variantInput?.value || Date.now();
                const identifier = `bis-button-${variantId}`;
                
                btn.setAttribute('data-a11y-identifier', identifier);
                
                // Also set on parent container/div if it exists
                if (parent && !parent.hasAttribute('data-a11y-identifier')) {
                  parent.setAttribute('data-a11y-identifier', identifier);
                }
                
                // Ensure clean URL
                if (cleanHandle) {
                  forceCleanKlaviyoURL(cleanHandle);
                }
                
                // Also ensure the button's parent div has the identifier if it's a wrapper
                const buttonWrapper = btn.closest('div[class*="klaviyo"], div[class*="bis"]');
                if (buttonWrapper && buttonWrapper !== parent && !buttonWrapper.hasAttribute('data-a11y-identifier')) {
                  buttonWrapper.setAttribute('data-a11y-identifier', identifier);
                }
              }
            });
          }
        });
      });
    });

    observer.observe(modal, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-a11y-identifier']
    });
    
    // Also periodically check for Klaviyo buttons that might have been injected without identifier
    let checkAttempts = 0;
    const maxAttempts = 15;
    const checkInterval = setInterval(() => {
      checkAttempts++;
      
      // Look for Klaviyo BIS buttons without identifier
      const klaviyoButtons = modal.querySelectorAll('#klaviyo-bis-button-container button, [class*="klaviyo"] button, [class*="bis"] button');
      klaviyoButtons.forEach(btn => {
        if (!btn.hasAttribute('data-a11y-identifier') && btn.textContent && btn.textContent.toLowerCase().includes('besked')) {
          const variantId = form.querySelector('input[name="id"]')?.value || Date.now();
          const identifier = `bis-button-${variantId}`;
          btn.setAttribute('data-a11y-identifier', identifier);
          
          // Also set on container if it exists
          const container = btn.closest('#klaviyo-bis-button-container, [class*="klaviyo"], [class*="bis"]');
          if (container && !container.hasAttribute('data-a11y-identifier')) {
            container.setAttribute('data-a11y-identifier', identifier);
          }
          
          // Ensure clean URL
          if (cleanHandle) {
            forceCleanKlaviyoURL(cleanHandle);
          }
        }
      });
      
      if (checkAttempts >= maxAttempts) {
        clearInterval(checkInterval);
      }
    }, 300);
  }

  // Handle variant changes in quickbuy modal
  function handleQuickbuyVariantChanges(modal) {
    if (!modal) return;

    const quickBuyContent = modal.querySelector('#quick-buy-modal-content');
    if (!quickBuyContent) return;
    
    // Get product handle for BIS detection
    const productHandle = modal.getAttribute('handle');
    let cleanHandle = productHandle;
    if (productHandle && productHandle.includes('/products/')) {
      const match = productHandle.match(/\/products\/([^\/\?]+)/);
      cleanHandle = match ? match[1] : productHandle;
    }

    // Find all disabled swatches (out of stock variants) in the quickbuy modal
    const blockSwatches = quickBuyContent.querySelectorAll('.block-swatch.is-disabled');
    
    blockSwatches.forEach(swatch => {
      // Skip if already processed
      if (swatch.hasAttribute('data-klaviyo-bis-processed')) return;
      swatch.setAttribute('data-klaviyo-bis-processed', 'true');

      // Add class for styling if needed
      swatch.classList.add('product-page-quickbuy-swatch', 'klaviyo-bis-variant-change');

      swatch.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get variant ID from the input inside the swatch
        const input = this.querySelector('input[data-variant-id]');
        if (!input) return;
        
        const variantId = input.getAttribute('data-variant-id');
        if (!variantId) return;

        // Find the form in the quickbuy modal
        const form = modal.querySelector('form[id*="product-form-quick-buy"]');
        if (!form) return;

        // Find the variant input in the form and trigger change
        const variantInput = form.querySelector(`input[data-variant-id="${variantId}"]`);
        if (variantInput) {
          // Trigger variant change
          variantInput.click();
          
          // After variant change, wait for rerender and re-initialize Klaviyo BIS
          setTimeout(() => {
            // Force clean URL and trigger BIS detection
            if (cleanHandle) {
              forceCleanKlaviyoURL(cleanHandle);
              triggerKlaviyoBISDetection(modal, cleanHandle);
            }
            initKlaviyoBISQuickbuy(modal);
            handleQuickbuyVariantChanges(modal);
          }, 300);
        }
      });
    });
  }

  // Initialize when quickbuy modal opens
  function setupQuickbuyModalListener() {
    // Listen for quickbuy modal show events
    document.addEventListener('dialog:after-show', function(e) {
      const modal = e.target;
      if (modal && modal.classList.contains('quick-buy-modal')) {
        setTimeout(() => {
          initKlaviyoBISQuickbuy(modal);
          handleQuickbuyVariantChanges(modal);
        }, 200);
      }
    });

    // Also check for existing modals
    const existingModals = document.querySelectorAll('quick-buy-modal[open]');
    existingModals.forEach(modal => {
      setTimeout(() => {
        initKlaviyoBISQuickbuy(modal);
        handleQuickbuyVariantChanges(modal);
      }, 200);
    });
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupQuickbuyModalListener);
  } else {
    setupQuickbuyModalListener();
  }

  // Also watch for dynamically added quickbuy modals
  const modalObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.tagName === 'QUICK-BUY-MODAL' || node.classList?.contains('quick-buy-modal')) {
            setTimeout(() => {
              initKlaviyoBISQuickbuy(node);
            }, 200);
          } else if (node.querySelectorAll) {
            const modals = node.querySelectorAll('quick-buy-modal');
            modals.forEach(modal => {
              setTimeout(() => {
                initKlaviyoBISQuickbuy(modal);
                handleQuickbuyVariantChanges(modal);
              }, 200);
            });
          }
        }
      });
    });
  });

  modalObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Handle triggerBIS URL parameter on product pages
  // This is used when navigating from inline quickbuy on collection pages
  function handleTriggerBISParameter() {
    console.log('handleTriggerBISParameter called');
    console.log('URL:', window.location.href);
    
    // Only run on product pages - check URL instead of body class
    const isProductPage = window.location.pathname.includes('/products/');
    if (!isProductPage) {
      console.log('Not a product page, skipping. URL:', window.location.pathname);
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const triggerBIS = urlParams.get('triggerBIS');
    const variantId = urlParams.get('variant');
    
    console.log('URL params:', { triggerBIS, variantId });
    
    if (triggerBIS !== 'true' || !variantId) {
      console.log('triggerBIS not true or variantId missing');
      return;
    }
    
    console.log('=== triggerBIS=true detected ===', { variantId });
    
    // Function to find and click the submit button
    const findAndClickButton = () => {
      // Find the button: .klaviyo-bis-trigger > button.buy-buttons__add-to-cart[data-variant-unavailable="true"]
      const klaviyoTrigger = document.querySelector('.klaviyo-bis-trigger');
      let button = null;
      
      if (klaviyoTrigger) {
        button = klaviyoTrigger.querySelector('button.buy-buttons__add-to-cart');
      }
      
      // Fallback: search more broadly
      if (!button) {
        button = document.querySelector('button.buy-buttons__add-to-cart[data-variant-unavailable="true"]');
      }
      
      if (button && button.isConnected) {
        console.log('✅ Button found! Clicking...', {
          text: button.textContent?.trim(),
          type: button.type
        });
        
        // Scroll into view
        button.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Wait a moment, then click
        setTimeout(() => {
          button.focus();
          
          setTimeout(() => {
            try {
              // Click the button
              button.click();
              
              // Also dispatch click event
              const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
                buttons: 1
              });
              button.dispatchEvent(clickEvent);
              
              console.log('✅ Button clicked!');
              
              // Clean up URL parameter
              setTimeout(() => {
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('triggerBIS');
                window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
              }, 1000);
            } catch (error) {
              console.error('❌ Error clicking button:', error);
            }
          }, 200);
        }, 500);
        
        return true;
      }
      
      return false;
    };
    
    // Try multiple times to find and click the button
    let attempts = 0;
    const maxAttempts = 30;
    const tryInterval = setInterval(() => {
      attempts++;
      console.log(`Attempt ${attempts} to find button...`);
      
      if (findAndClickButton()) {
        clearInterval(tryInterval);
      } else if (attempts >= maxAttempts) {
        clearInterval(tryInterval);
        console.log('❌ Could not find button after ' + maxAttempts + ' attempts');
        
        // Last resort: try Klaviyo API
        if (window._klOnsite && window._klOnsite.openForm) {
          console.log('Trying Klaviyo API as last resort...');
          window._klOnsite.openForm(['TDfjf9']);
        }
        
        // Clean up URL parameter
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('triggerBIS');
        window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
      }
    }, 300);
        
        // Function to trigger BIS popup by clicking the button
        const triggerBISPopup = () => {
          console.log('=== triggerBISPopup called ===');
          console.log('Variant ID from URL:', variantId);
          
          // Function to click Klaviyo BIS button
          const clickKlaviyoBISButton = (button) => {
            if (!button || button.offsetParent === null) return false;
            
            console.log('Clicking Klaviyo BIS button:', {
              button: button,
              text: button.textContent?.trim(),
              identifier: button.getAttribute('data-a11y-identifier'),
              isConnected: button.isConnected,
              offsetParent: button.offsetParent !== null
            });
            
            // Scroll button into view
            button.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            setTimeout(() => {
              // Focus and click
              button.focus();
              
              setTimeout(() => {
                try {
                  // Click the button multiple ways
                  button.click();
                  
                  // Also dispatch click event
                  const clickEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    buttons: 1
                  });
                  button.dispatchEvent(clickEvent);
                  
                  // Also try mousedown/mouseup
                  const mouseDownEvent = new MouseEvent('mousedown', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    buttons: 1
                  });
                  const mouseUpEvent = new MouseEvent('mouseup', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    buttons: 1
                  });
                  button.dispatchEvent(mouseDownEvent);
                  button.dispatchEvent(mouseUpEvent);
                  
                  console.log('Clicked Klaviyo BIS button (all methods)');
                  
                  // Clean up URL parameter
                  const newUrl = new URL(window.location.href);
                  newUrl.searchParams.delete('triggerBIS');
                  window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
                  
                  return true;
                } catch (error) {
                  console.error('Error clicking Klaviyo BIS button:', error);
                  return false;
                }
              }, 200);
            }, 300);
            
            return true;
          };
          
          // FIRST: Try Add to Cart button method immediately (this is the button that triggers Klaviyo BIS)
          // The button structure is: .klaviyo-bis-trigger > button.buy-buttons__add-to-cart[data-variant-unavailable="true"]
          console.log('=== Starting to look for Add to Cart button ===');
          
          // Function to try Add to Cart button method
          const tryAddToCartMethod = () => {
            console.log('=== tryAddToCartMethod called ===');
            
            // Find the specific button: .klaviyo-bis-trigger > button.buy-buttons__add-to-cart[data-variant-unavailable="true"]
            const klaviyoTrigger = document.querySelector('.klaviyo-bis-trigger');
            console.log('klaviyoTrigger found:', klaviyoTrigger);
            
            let addToCartButton = null;
            
            if (klaviyoTrigger) {
              addToCartButton = klaviyoTrigger.querySelector('button.buy-buttons__add-to-cart');
              console.log('addToCartButton found in klaviyoTrigger:', addToCartButton);
            }
            
            // Fallback: search more broadly if not found
            if (!addToCartButton) {
              console.log('Button not found in klaviyo-bis-trigger, searching more broadly...');
              addToCartButton = document.querySelector('button.buy-buttons__add-to-cart[data-variant-unavailable="true"]');
              console.log('Found button directly:', addToCartButton);
            }
            
            if (addToCartButton) {
              // Check if button has data-variant-unavailable attribute
              const isUnavailable = addToCartButton.hasAttribute('data-variant-unavailable');
              
              console.log('Found Add to Cart button:', {
                button: addToCartButton,
                isUnavailable: isUnavailable,
                buttonText: addToCartButton.textContent?.trim(),
                buttonType: addToCartButton.type,
                hasDataVariantUnavailable: addToCartButton.hasAttribute('data-variant-unavailable'),
                parent: addToCartButton.parentElement?.className
              });
              
              if (isUnavailable) {
                console.log('✅ Button is unavailable - clicking it now');
                clickTheButton(addToCartButton);
              } else {
                console.log('⚠️ Button does not have data-variant-unavailable attribute, but clicking anyway');
                clickTheButton(addToCartButton);
              }
            } else {
              console.log('❌ Add to Cart button not found anywhere');
              
              // Last resort: try to trigger via Klaviyo API
              if (window._klOnsite && window._klOnsite.openForm) {
                console.log('Triggering Klaviyo BIS via API (last resort)');
                window._klOnsite.openForm(['TDfjf9']);
              }
              
              // Clean up URL parameter
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.delete('triggerBIS');
              window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
            }
          };
          
          // Function to actually click the button
          const clickTheButton = (button) => {
            console.log('=== clickTheButton called ===', button);
            
            if (!button) {
              console.log('No button provided to clickTheButton');
              return;
            }
            
            // Check if button is in DOM and visible
            if (!button.isConnected) {
              console.log('Button is not connected to DOM, retrying...');
              setTimeout(() => triggerBISPopup(), 500);
              return;
            }
            
            if (button.offsetParent === null) {
              console.log('Button is not visible (offsetParent is null), scrolling into view...');
            }
            
            console.log('Button details:', {
              text: button.textContent?.trim(),
              type: button.type,
              isConnected: button.isConnected,
              offsetParent: button.offsetParent !== null,
              className: button.className,
              id: button.id
            });
            
            // Scroll button into view to ensure it's clickable
            button.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Wait a moment for scroll, then click
            setTimeout(() => {
              // Focus the button first to ensure it's ready
              button.focus();
              
              // Wait a tiny bit more
              setTimeout(() => {
                console.log('About to click button...');
                
                // Try clicking multiple ways to ensure it works
                try {
                  // Method 1: Native click
                  console.log('Method 1: Native click()');
                  button.click();
                  
                  // Method 2: Dispatch click event
                  console.log('Method 2: MouseEvent click');
                  const clickEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    buttons: 1
                  });
                  button.dispatchEvent(clickEvent);
                  
                  // Method 3: mousedown + mouseup
                  console.log('Method 3: mousedown + mouseup');
                  const mouseDownEvent = new MouseEvent('mousedown', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    buttons: 1
                  });
                  const mouseUpEvent = new MouseEvent('mouseup', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    buttons: 1
                  });
                  button.dispatchEvent(mouseDownEvent);
                  button.dispatchEvent(mouseUpEvent);
                  
                  console.log('✅ Clicked button with all methods');
                  
                  // Clean up URL parameter after a delay
                  setTimeout(() => {
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('triggerBIS');
                    window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
                  }, 1000);
                } catch (error) {
                  console.error('❌ Error clicking button:', error);
                }
              }, 100);
            }, 500);
          };
          
          // Start trying Add to Cart method immediately (with a small delay for page to be ready)
          setTimeout(() => {
            tryAddToCartMethod();
          }, 500);
          
          // SECOND: Also check if Klaviyo BIS button appears separately (in case it's injected)
          let bisButtonFound = false;
          let checkBISAttempts = 0;
          const maxBISAttempts = 15;
          const checkBISInterval = setInterval(() => {
            checkBISAttempts++;
            
            const klaviyoBISButton = document.querySelector('button[data-a11y-identifier*="bis-button"]');
            
            if (klaviyoBISButton && klaviyoBISButton.offsetParent !== null) {
              console.log('Klaviyo BIS button found separately, clicking directly');
              clearInterval(checkBISInterval);
              bisButtonFound = true;
              clickKlaviyoBISButton(klaviyoBISButton);
            } else if (checkBISAttempts >= maxBISAttempts) {
              clearInterval(checkBISInterval);
            }
          }, 200);
    }
  }

  // Wait for Klaviyo to be ready
  function waitForKlaviyo(callback) {
    if (window._klOnsite && window._klOnsite.page) {
      callback();
    } else {
      let attempts = 0;
      const maxAttempts = 50;
      const interval = setInterval(() => {
        if (window._klOnsite && window._klOnsite.page) {
          clearInterval(interval);
          callback();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          console.warn('Klaviyo _klOnsite object not found after multiple attempts.');
          callback(); // Continue anyway
        }
        attempts++;
      }, 100);
    }
  }

  // Initialize triggerBIS handler
  console.log('=== klaviyo-bis-quickbuy.js loaded ===');
  console.log('Document readyState:', document.readyState);
  console.log('Body:', document.body);
  console.log('Body classes:', document.body?.className);
  console.log('URL:', window.location.href);
  
  const initTriggerBIS = () => {
    console.log('=== initTriggerBIS called ===');
    console.log('Body at call time:', document.body);
    console.log('Body classes at call time:', document.body?.className);
    handleTriggerBISParameter();
  };
  
  // Try multiple times to ensure body is ready
  let initAttempts = 0;
  const maxInitAttempts = 10;
  const tryInit = () => {
    initAttempts++;
    console.log(`Init attempt ${initAttempts}`);
    
    if (document.body && document.body.classList) {
      console.log('Body is ready, calling initTriggerBIS');
      initTriggerBIS();
    } else if (initAttempts < maxInitAttempts) {
      console.log('Body not ready yet, retrying...');
      setTimeout(tryInit, 200);
    } else {
      console.log('Body not ready after max attempts, calling anyway');
      initTriggerBIS();
    }
  };
  
  // Always wait a bit to ensure everything is ready, especially with defer
  if (document.readyState === 'loading') {
    console.log('Document still loading, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(tryInit, 500);
    });
  } else {
    console.log('Document already loaded, starting init attempts');
    setTimeout(tryInit, 500);
  }

})();

